import { create } from 'zustand';
import { ApiError } from '@/api/errors';

export type ConnectivityState = 'online' | 'probing' | 'awaiting_user_decision';

type ConnectivityStore = {
  state: ConnectivityState;
  shouldShowOverlay: boolean;
  pendingUnsafeRequests: number;
  slow: boolean;
  veryLong: boolean;
  _setState: (s: Partial<ConnectivityStore>) => void;
};

export const useConnectivityStore = create<ConnectivityStore>((set) => ({
  state: 'online',
  shouldShowOverlay: false,
  pendingUnsafeRequests: 0,
  slow: false,
  veryLong: false,
  _setState: (s) => set(s),
}));

type SafeWaiter = {
  resolve: () => void;
  reject: (err: unknown) => void;
  signal?: AbortSignal;
  onAbort?: () => void;
};

type UnsafeWaiter = {
  resolve: (decision: 'retry' | 'cancel') => void;
  reject: (err: unknown) => void;
  signal?: AbortSignal;
  onAbort?: () => void;
};

const safeWaiters: SafeWaiter[] = [];
const unsafeWaiters: UnsafeWaiter[] = [];

const PROBE_INTERVAL_MS = 2000;
const OVERLAY_DELAY_MS = 800;
const SLOW_FLAG_MS = 60_000;
const VERY_LONG_MS = 300_000;
const PROBE_TIMEOUT_MS = 2000;

let probeTimer: ReturnType<typeof setInterval> | null = null;
let overlayTimer: ReturnType<typeof setTimeout> | null = null;
let slowTimer: ReturnType<typeof setTimeout> | null = null;
let veryLongTimer: ReturnType<typeof setTimeout> | null = null;
let probeInFlight = false;

function syncPendingCount() {
  useConnectivityStore.getState()._setState({
    pendingUnsafeRequests: unsafeWaiters.length,
  });
}

function clearTimers() {
  if (probeTimer) {
    clearInterval(probeTimer);
    probeTimer = null;
  }
  if (overlayTimer) {
    clearTimeout(overlayTimer);
    overlayTimer = null;
  }
  if (slowTimer) {
    clearTimeout(slowTimer);
    slowTimer = null;
  }
  if (veryLongTimer) {
    clearTimeout(veryLongTimer);
    veryLongTimer = null;
  }
}

function startTimers() {
  if (overlayTimer || slowTimer || veryLongTimer) return;
  overlayTimer = setTimeout(() => {
    useConnectivityStore.getState()._setState({ shouldShowOverlay: true });
  }, OVERLAY_DELAY_MS);
  slowTimer = setTimeout(() => {
    useConnectivityStore.getState()._setState({ slow: true });
  }, SLOW_FLAG_MS);
  veryLongTimer = setTimeout(() => {
    useConnectivityStore.getState()._setState({ veryLong: true });
  }, VERY_LONG_MS);
}

function transitionToOnline() {
  clearTimers();
  useConnectivityStore.getState()._setState({
    state: 'online',
    shouldShowOverlay: false,
    slow: false,
    veryLong: false,
  });
}

function transitionToProbing() {
  const current = useConnectivityStore.getState().state;
  if (current === 'probing') return;
  useConnectivityStore.getState()._setState({ state: 'probing' });
  startTimers();
  if (!probeTimer) {
    probeTimer = setInterval(() => {
      void runProbe();
    }, PROBE_INTERVAL_MS);
  }
}

function transitionToAwaitingUserDecision() {
  if (probeTimer) {
    clearInterval(probeTimer);
    probeTimer = null;
  }
  useConnectivityStore.getState()._setState({ state: 'awaiting_user_decision' });
}

async function runProbe(): Promise<void> {
  if (probeInFlight) return;
  probeInFlight = true;
  try {
    const ctrl = new AbortController();
    const timeoutId = setTimeout(() => ctrl.abort(), PROBE_TIMEOUT_MS);
    let ok = false;
    try {
      const res = await fetch('/api/health', { signal: ctrl.signal });
      ok = res.ok;
    } catch {
      ok = false;
    } finally {
      clearTimeout(timeoutId);
    }
    if (ok) {
      onProbeSuccess();
    }
  } finally {
    probeInFlight = false;
  }
}

function onProbeSuccess() {
  // Resolve all safe waiters — connection is back, they can replay.
  const safeBatch = safeWaiters.splice(0, safeWaiters.length);
  for (const w of safeBatch) {
    if (w.onAbort && w.signal) w.signal.removeEventListener('abort', w.onAbort);
    w.resolve();
  }
  if (unsafeWaiters.length > 0) {
    transitionToAwaitingUserDecision();
  } else {
    transitionToOnline();
  }
}

function detachWaiter(arr: { signal?: AbortSignal; onAbort?: () => void }[], idx: number) {
  const w = arr[idx];
  if (w?.onAbort && w.signal) w.signal.removeEventListener('abort', w.onAbort);
  arr.splice(idx, 1);
}

function attachAbort(
  arr: { signal?: AbortSignal; onAbort?: () => void }[],
  waiter: { signal?: AbortSignal; onAbort?: () => void },
  onAbortAction: () => void,
) {
  if (!waiter.signal) return;
  const handler = () => {
    const idx = arr.indexOf(waiter);
    if (idx === -1) return;
    detachWaiter(arr, idx);
    onAbortAction();
  };
  waiter.onAbort = handler;
  waiter.signal.addEventListener('abort', handler);
}

function makeAbortError(): ApiError {
  return new ApiError('REQUEST_ABORTED', 'Verzoek afgebroken', 0);
}

export const connectivity = {
  /** Used by safe (GET/HEAD) or retrySafe requests. Resolves once probe recovers. */
  waitForReconnect(signal?: AbortSignal): Promise<void> {
    if (signal?.aborted) return Promise.reject(makeAbortError());
    return new Promise<void>((resolve, reject) => {
      const waiter: SafeWaiter = { resolve, reject, signal };
      safeWaiters.push(waiter);
      attachAbort(safeWaiters, waiter, () => reject(makeAbortError()));
      transitionToProbing();
    });
  },

  /**
   * Used by unsafe (POST/PUT/PATCH/DELETE with JSON body) requests.
   * Resolves with 'retry' or 'cancel' only after the user makes a choice
   * in the overlay (after the probe has confirmed connectivity).
   */
  waitForUserDecision(signal?: AbortSignal): Promise<'retry' | 'cancel'> {
    if (signal?.aborted) return Promise.reject(makeAbortError());
    return new Promise<'retry' | 'cancel'>((resolve, reject) => {
      const waiter: UnsafeWaiter = { resolve, reject, signal };
      unsafeWaiters.push(waiter);
      syncPendingCount();
      attachAbort(unsafeWaiters, waiter, () => {
        syncPendingCount();
        // If aborting empties the unsafe queue while we were waiting on user,
        // and no safe waiters remain, return to online.
        if (
          unsafeWaiters.length === 0 &&
          safeWaiters.length === 0 &&
          useConnectivityStore.getState().state === 'awaiting_user_decision'
        ) {
          transitionToOnline();
        }
        reject(makeAbortError());
      });
      transitionToProbing();
    });
  },

  /** Caller-confirmed online (a normal response came back). No-op if already online. */
  reportSuccess(): void {
    const s = useConnectivityStore.getState().state;
    if (s !== 'online') {
      // Only flip if there are no parked waiters expecting decisions —
      // a single ad-hoc success doesn't override a deploy-class failure batch.
      // In practice client.ts calls this on every 2xx so it's safe to only
      // transition when nothing is waiting.
      if (safeWaiters.length === 0 && unsafeWaiters.length === 0) {
        transitionToOnline();
      }
    }
  },

  /** User clicked "Opnieuw proberen". Resolves all parked unsafe waiters with 'retry'. */
  userClickRetry(): void {
    if (useConnectivityStore.getState().state !== 'awaiting_user_decision') return;
    const batch = unsafeWaiters.splice(0, unsafeWaiters.length);
    syncPendingCount();
    for (const w of batch) {
      if (w.onAbort && w.signal) w.signal.removeEventListener('abort', w.onAbort);
      w.resolve('retry');
    }
    if (safeWaiters.length === 0) {
      transitionToOnline();
    } else {
      transitionToProbing();
    }
  },

  /** User clicked "Annuleren". Rejects all parked unsafe waiters with NETWORK_INTERRUPTED. */
  userClickCancel(): void {
    if (useConnectivityStore.getState().state !== 'awaiting_user_decision') return;
    const batch = unsafeWaiters.splice(0, unsafeWaiters.length);
    syncPendingCount();
    for (const w of batch) {
      if (w.onAbort && w.signal) w.signal.removeEventListener('abort', w.onAbort);
      w.resolve('cancel');
    }
    if (safeWaiters.length === 0) {
      transitionToOnline();
    } else {
      transitionToProbing();
    }
  },

  /** Test-only helpers — not part of the runtime API. */
  __testing: {
    runProbeNow: runProbe,
    reset(): void {
      clearTimers();
      probeInFlight = false;
      safeWaiters.splice(0, safeWaiters.length);
      unsafeWaiters.splice(0, unsafeWaiters.length);
      useConnectivityStore.setState({
        state: 'online',
        shouldShowOverlay: false,
        pendingUnsafeRequests: 0,
        slow: false,
        veryLong: false,
      });
    },
    safeWaiterCount: () => safeWaiters.length,
    unsafeWaiterCount: () => unsafeWaiters.length,
  },
};
